// ── useCurrentContractor (P11a + P12.2 observer-aware) ──
// React hook over the canonical resolver. Re-resolves on:
//   • AppData contractor list change
//   • cross-tab `storage` event for CONTRACTOR_SESSION_KEY
//   • manual `refresh()` call
//
// P12.2: When SuperAdmin is in Observer Mode (sessionStorage observer id),
// this hook returns the OBSERVED contractor instead of the localStorage
// session contractor. It does NOT write localStorage and does NOT mutate
// the canonical session.

import { useCallback, useEffect, useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import {
  resolveCurrentContractor,
  CONTRACTOR_SESSION_KEY,
  CONTRACTOR_OVERRIDE_KEY,
  type ResolvedContractorIdentity,
} from "@/data/contractor/currentContractor";
import { useObserverMode } from "@/hooks/useObserverMode";

export type CurrentContractor = ResolvedContractorIdentity & {
  refresh: () => void;
  /** True when the resolved identity comes from SuperAdmin observation. */
  isObserved?: boolean;
};

export function useCurrentContractor(): CurrentContractor {
  const { contractors } = useAppData();
  const [tick, setTick] = useState(0);
  const observer = useObserverMode("contractor");

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (
        e.key === CONTRACTOR_SESSION_KEY ||
        e.key === CONTRACTOR_OVERRIDE_KEY ||
        e.key === null
      ) {
        refresh();
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refresh]);

  // tick is a dep so refresh() forces re-resolution
  void tick;

  // Observer mode short-circuits the canonical resolver.
  if (observer.isObserverMode && observer.observedId) {
    const record = contractors.find((c) => c.id === observer.observedId);
    if (record) {
      return {
        id: record.id,
        record,
        source: "auth",
        displayName: record.companyName ?? record.id,
        matchKey: record.companyName ?? record.id,
        firstName:
          record.contactFirstName?.trim() ||
          record.contactName?.trim().split(/\s+/)[0] ||
          undefined,
        isUnresolved: false,
        isObserved: true,
        refresh,
      };
    }
  }

  const identity = resolveCurrentContractor(contractors);
  return { ...identity, refresh, isObserved: false };
}
